import cx from 'classnames';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { CheckboxMenuItem, StaticMenuItem } from '../../../generic';
import Tooltip from '../../../generic/Tooltip';

import { TEAM_NAME_LENGTH_LIMIT } from './common';

import styles from './TeamSelectionMenu.module.less';

//  Returns the processed name and whether it should be truncated and tooltipified
const getTruncatedName = (name: string, shouldTruncate?: boolean) => {
  if (!shouldTruncate) {
    return { shouldTruncateAndTooltip: false, processedName: name };
  }

  const shouldTruncateAndTooltip = name.length > TEAM_NAME_LENGTH_LIMIT;
  const processedName = shouldTruncateAndTooltip
    ? `${name.substring(0, TEAM_NAME_LENGTH_LIMIT)}...`
    : name;
  return { shouldTruncateAndTooltip, processedName };
};

type Props = {
  name: string;
  count?: number;
  isSelected: boolean;
  onClick: () => void;
  //  Option to skip the truncation and tooltipification of the name
  shouldTruncate?: boolean;
};

/**
 * A team menu item that can be a checkbox or a static item.
 */
const TeamMenuItem: VoidFunctionComponent<Props> = ({
  name,
  count,
  isSelected,
  onClick,
  shouldTruncate = true,
}) => {
  const { shouldTruncateAndTooltip, processedName } = getTruncatedName(name, shouldTruncate);
  return (
    <Tooltip
      id={`team-selection-menu-item-${name}`}
      tooltip={name}
      showTooltip={shouldTruncateAndTooltip}
    >
      <CheckboxMenuItem
        className={cx(styles.item, {
          [styles.itemHasCount]: !_.isNil(count),
        })}
        onClick={(e) => {
          // This is the recommended way to handle keepOpen:
          // https://szhsin.github.io/react-menu/#event-handling
          // eslint-disable-next-line no-param-reassign
          e.keepOpen = true;
          //  Note: this is optional only so that it can be used for the label of a submenu
          //  In that case, the onClick is handled a different way, but we still want the
          //  root submenu item to have regular styling.
          onClick?.();
        }}
        count={count}
        checked={isSelected}
      >
        <span className={cx(styles.itemName, {
          [styles.itemNameSelected]: isSelected,
        })}>
          {processedName}
        </span>
      </CheckboxMenuItem>
    </Tooltip>
  );
};

type StaticTeamMenuItemProps = Omit<Props, 'onClick'> & {
  hasSubMenu?: boolean;
};

/**
 * A static team menu item that does not have a click handler / is not a CheckboxMenuItem
 */
export const StaticTeamMenuItem: VoidFunctionComponent<StaticTeamMenuItemProps> = ({
  name,
  count,
  isSelected,
  shouldTruncate = true,
  hasSubMenu = false,
}) => {
  const { shouldTruncateAndTooltip, processedName } = getTruncatedName(name, shouldTruncate);
  return (
    <Tooltip
      id={`team-selection-menu-item-${name}`}
      tooltip={name}
      showTooltip={shouldTruncateAndTooltip}
    >
      <StaticMenuItem
        className={cx(styles.item, {
          [styles.itemHasCount]: !hasSubMenu,
        })}
        checked={isSelected}
        count={count}
      >
        <span className={styles.itemName}>{processedName}</span>
      </StaticMenuItem>
    </Tooltip>
  );
};

export default TeamMenuItem;
